import React, { useEffect, useRef, useState } from 'react';
import { Heart, Loader2, Send, Sparkles, X } from 'lucide-react';
import client from '../api/client';

const STARTER_PROMPTS = [
  'What should I tackle this week?',
  'Help me word my Save the Date',
  'Is my budget realistic?',
];

const WELCOME = {
  role: 'ai',
  text: "Hi — I'm your wedding friend. Ask me anything: budget, wording, vendors, etiquette. No question is too small.",
};

export default function FloatingAIBubble() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      endRef.current?.scrollIntoView({ behavior: 'smooth' });
      // small delay so the panel animates before focus
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open, messages, typing]);

  const send = async (text) => {
    const value = (text ?? input).trim();
    if (!value || typing) return;
    setMessages((m) => [...m, { role: 'user', text: value }]);
    setInput('');
    setTyping(true);
    try {
      const { data } = await client.post('/ai/chat', { sessionId, message: value });
      if (!sessionId) setSessionId(data.sessionId);
      setMessages((m) => [...m, { role: 'ai', text: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'ai', text: "I'm having a tiny moment — please try again in a few seconds." }]);
    } finally {
      setTyping(false);
    }
  };

  return (
    <>
      {/* Bubble launcher */}
      {!open && (
        <button
          data-testid="floating-ai-bubble-open"
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-6 z-50 group"
          aria-label="Open your wedding friend"
        >
          <span className="absolute -inset-2 rounded-full bg-[#7B2C3B]/20 blur-xl opacity-60 group-hover:opacity-100 transition-opacity" />
          <span className="relative flex items-center gap-2 px-4 py-3 rounded-full bg-[#7B2C3B] text-[#FBF7F2] shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5">
            <span className="relative">
              <Sparkles className="w-5 h-5" />
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#F4D6CE] animate-pulse" />
            </span>
            <span className="hidden sm:inline text-sm font-medium">Ask your wedding friend</span>
          </span>
        </button>
      )}

      {/* Expanded chat panel */}
      {open && (
        <div
          data-testid="floating-ai-panel"
          className="fixed bottom-24 right-6 z-50 w-[min(92vw,380px)] h-[min(72vh,540px)] rounded-3xl bg-[#FBF7F2] border border-[#EFE3D8] shadow-2xl flex flex-col overflow-hidden animate-float-up"
        >
          <div className="px-4 py-3 border-b border-[#EFE3D8] bg-white flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-[#F4D6CE] flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-[#7B2C3B]" />
              </div>
              <div className="min-w-0">
                <p className="font-serif text-base text-[#2A2A2A] leading-tight">Your wedding friend</p>
                <p className="text-[10px] text-[#9A8A7E]">Knows your dates, budget &amp; guests</p>
              </div>
            </div>
            <button
              data-testid="floating-ai-bubble-close"
              onClick={() => setOpen(false)}
              className="w-8 h-8 rounded-full hover:bg-[#F4E4DA] flex items-center justify-center text-[#6A6A6A]"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-gradient-to-b from-[#FBF7F2] to-[#F6EAE0]/40">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === 'user'
                      ? 'bg-[#7B2C3B] text-[#FBF7F2] rounded-br-sm'
                      : 'bg-white border border-[#EFE3D8] text-[#2A2A2A] rounded-bl-sm'
                  }`}
                >
                  {m.role === 'ai' && (
                    <span className="flex items-center gap-1.5 mb-1 text-[10px] text-[#B8896C]">
                      <Heart className="w-2.5 h-2.5 fill-[#B8896C]" /> wedding friend
                    </span>
                  )}
                  {m.text}
                </div>
              </div>
            ))}
            {typing && (
              <div className="flex justify-start">
                <div className="bg-white border border-[#EFE3D8] rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-xs text-[#6A6A6A] inline-flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" /> thinking…
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {messages.length <= 1 && (
            <div className="px-4 pt-2 flex flex-wrap gap-1.5">
              {STARTER_PROMPTS.map((p, i) => (
                <button
                  key={i}
                  data-testid={`floating-ai-suggestion-${i}`}
                  onClick={() => send(p)}
                  disabled={typing}
                  className="text-[11px] px-2.5 py-1 rounded-full bg-[#F4E4DA] text-[#7B2C3B] hover:bg-[#EBD3C2] transition-colors disabled:opacity-50"
                >
                  {p}
                </button>
              ))}
            </div>
          )}

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="p-3 border-t border-[#EFE3D8] bg-[#FBF7F2] flex items-center gap-2"
          >
            <input
              ref={inputRef}
              data-testid="floating-ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything…"
              className="flex-1 px-3.5 py-2.5 rounded-full border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
            />
            <button
              type="submit"
              data-testid="floating-ai-send"
              disabled={typing}
              className="btn-primary w-10 h-10 rounded-full flex items-center justify-center disabled:opacity-60"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
